import { useCallback, useEffect, useMemo, useState } from 'react'

import useAddGlGroup, {
  IDataType as IAddGlGroupDataType,
  IOptionType as IAddGlGroupOptionType,
} from './useAddGlGroup'
import useEditGlGroup, {
  IDataType as IEditGlGroupDataType,
  IOptionType as IEditGlGroupOptionType,
} from './useEditGlGroup'
import useListGlCodesAndGroups, {
  IQueryDataType,
} from './useListGlCodesAndGroups'
import usePnlDrop, { IDataType as IPnlDropDataType } from './usePnlDrop'

type IStateType = IAddGlGroupOptionType | IEditGlGroupOptionType

export interface IDataType {
  addGlGroup: IAddGlGroupDataType | undefined
  editGlGroup: IEditGlGroupDataType | undefined
  pnlDrop: IPnlDropDataType | undefined
  pnlCategories: {
    id: string
    parentId: string
    displayName: string
  }[]
  manageGls: {
    isFormulaGroup: boolean
    addGlGroupOpen: boolean
    data: IQueryDataType | undefined
    editGlGroupOpen: boolean
    editMode: boolean
    loading: boolean
    onClose: () => void
    onOpenAdd: (options: { values: IAddGlGroupOptionType }) => void
    onOpenEdit: (options: { values: IEditGlGroupOptionType }) => void
    onOpenAddFormulaGroup: () => void
    onToggleEditMode: () => void
  }
}

const useManageGls = (): IDataType => {
  const editModeHook = useState(false)
  const manageHook = useState<IStateType | null>(null)
  const onClose = useCallback(() => manageHook[1](null), [manageHook])
  const { data, loading } = useListGlCodesAndGroups()
  const pnlCategories = useMemo(
    () => [
      ...(data?.listGlGroups.nodes || [])
        .filter((l) => !l.formula)
        .map((l) => ({
          id: `folder-${l.id}`,
          parentId: 'root',
          displayName: `Categories > ${l.name}`,
        })),
      ...(data?.listGlCodes.nodes || []).map((l) => ({
        id: `item-${l.id}`,
        parentId: 'root',
        displayName: `GL Code > ${l.code}-${l.name}`,
      })),
    ],
    [data],
  )

  const addGlGroup = useAddGlGroup(
    data || null,
    manageHook[0] as IAddGlGroupOptionType,
    onClose,
  )

  const editGlGroup = useEditGlGroup(
    data || null,
    manageHook[0] as IEditGlGroupOptionType,
    onClose,
  )

  const pnlDrop = usePnlDrop(data || null)

  return {
    addGlGroup,
    editGlGroup,
    pnlDrop: editModeHook[0] ? pnlDrop : undefined,
    pnlCategories,
    manageGls: useMemo((): IDataType['manageGls'] => {
      const [manageState, setManageState] = manageHook

      return {
        isFormulaGroup: Boolean(
          manageState &&
            'isFormulaGroup' in manageState &&
            manageState.isFormulaGroup,
        ),
        loading,
        addGlGroupOpen: manageState?.manageType === 'addGlGroup',
        editGlGroupOpen: manageState?.manageType === 'editGlGroup',
        onOpenAdd: ({ values }) => {
          setManageState({
            ...values,
            manageType: 'addGlGroup',
          })
        },
        onOpenEdit: ({ values }) => {
          setManageState({
            ...values,
            manageType: 'editGlGroup',
          })
        },
        onOpenAddFormulaGroup: () => {
          setManageState({
            isFormulaGroup: true,
            manageType: 'addGlGroup',
          })
        },
        onClose,
        data,
        editMode: editModeHook[0],
        onToggleEditMode: () => {
          editModeHook[1](!editModeHook[0])
        },
      }
    }, [manageHook, onClose]),
  }
}

export default useManageGls
